import {
  createSignal,
  createEffect,
  For,
  Show,
  on,
  createMemo,
  onCleanup,
  untrack,
} from "solid-js";
import clsx from "~/util/html/style";
import type { FormData, TimeSlot, AppointmentTemplate } from "./types";
import { fetchWeekSlots, placeHold } from "./api";
import { useI18n, dateLocale } from "./i18n";
import { localeDate, localeTime } from "~/util/date/intl_dates";

// ── Timezone-aware helpers ──────────────────────────────────────────────

function formatTimeInZone(iso: string, timezone: string, locale: string): string {
  const dl = dateLocale(locale);
  return localeTime(dl, new Date(iso), {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  });
}

function getHourInZone(iso: string, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    hour12: false,
    timeZone: timezone,
  }).formatToParts(new Date(iso));
  return parseInt(parts.find((p) => p.type === "hour")!.value, 10);
}

/** Minutes since midnight in the given timezone (for sorting) */
function getMinutesSinceMidnight(iso: string, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: false,
    timeZone: timezone,
  }).formatToParts(new Date(iso));
  const h = parseInt(parts.find((p) => p.type === "hour")!.value, 10);
  const m = parseInt(parts.find((p) => p.type === "minute")!.value, 10);
  return h * 60 + m;
}

/** Today's date as YYYY-MM-DD in the given timezone */
function todayInZone(timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")!.value;
  const m = parts.find((p) => p.type === "month")!.value;
  const d = parts.find((p) => p.type === "day")!.value;
  return `${y}-${m}-${d}`;
}

/** YYYY-MM-DD from a Date using local (not UTC) getters */
function toLocalDateISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateShort(iso: string, locale: string): string {
  const dl = dateLocale(locale);
  const date = new Date(iso + "T12:00:00");
  return localeDate(dl, date, { weekday: "short", month: "short", day: "numeric" });
}

// ── Swipe hook ──────────────────────────────────────────────────────────

function useSwipe(
  ref: () => HTMLElement | undefined,
  onSwipe: (direction: "left" | "right") => void,
) {
  let startX = 0;
  let startY = 0;
  let tracking = false;

  const THRESHOLD = 40;

  const handleStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    startX = touch.clientX;
    startY = touch.clientY;
    tracking = true;
  };

  const handleEnd = (e: TouchEvent) => {
    if (!tracking) return;
    tracking = false;
    const touch = e.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;
    if (Math.abs(dx) > THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5) {
      onSwipe(dx < 0 ? "left" : "right");
    }
  };

  createEffect(() => {
    const el = ref();
    if (!el) return;
    el.addEventListener("touchstart", handleStart, { passive: true });
    el.addEventListener("touchend", handleEnd, { passive: true });
    onCleanup(() => {
      el.removeEventListener("touchstart", handleStart);
      el.removeEventListener("touchend", handleEnd);
    });
  });
}

// ── Grouped slot type ───────────────────────────────────────────────────

type GroupedSlot = {
  time: string;
  hour: number;
  sortMinutes: number;
  /** All raw slots at this time — system picks the first one */
  slots: TimeSlot[];
  key: string;
};

// ── Main component ──────────────────────────────────────────────────────

export function AppointmentTimeStep(props: {
  formData: FormData;
  onUpdate: (partial: Partial<FormData>) => void;
  onNext: () => void;
  onBack: () => void;
  locationId: string;
  selectedTemplate: AppointmentTemplate | null;
  timezone: string;
  locale: string;
  slotHoldsEnabled: boolean;
}) {
  const t = useI18n();
  const dl = () => dateLocale(props.locale);

  const [weekSlots, setWeekSlots] = createSignal<Record<string, TimeSlot[]>>({});
  const [initialLoading, setInitialLoading] = createSignal(true);
  const [loading, setLoading] = createSignal(true);
  const [nextAvailableDate, setNextAvailableDate] = createSignal<string | null>(null);
  const [holdingSlot, setHoldingSlot] = createSignal<string | null>(null);
  const [holdError, setHoldError] = createSignal<string | null>(null);
  // Bumped after a lost race so the grid drops the time someone else took.
  const [reloadToken, setReloadToken] = createSignal(0);

  const [selectedDate, setSelectedDate] = createSignal(
    untrack(() => props.formData.appointmentDate),
  );

  // Separate signal for which week is being viewed (a Sunday ISO date)
  const getSundayOf = (iso: string) => {
    const d = new Date(iso + "T12:00:00");
    d.setDate(d.getDate() - d.getDay());
    return toLocalDateISO(d);
  };

  const [viewWeekStart, setViewWeekStart] = createSignal(getSundayOf(untrack(selectedDate)));

  const slots = createMemo(() => weekSlots()[selectedDate()] ?? []);

  const weekAvailability = createMemo(() => {
    const ws = weekSlots();
    const result: Record<string, number> = {};
    for (const [date, daySlots] of Object.entries(ws)) {
      result[date] = daySlots.length;
    }
    return result;
  });

  createEffect(() => {
    if (selectedDate() !== props.formData.appointmentDate) {
      props.onUpdate({ appointmentDate: selectedDate() });
    }
  });

  // Build week days from viewWeekStart (independent of selectedDate)
  const weekDays = createMemo(() => {
    const sunday = new Date(viewWeekStart() + "T12:00:00");

    const days: {
      date: string;
      dayName: string;
      dayNum: number;
      isToday: boolean;
      isPast: boolean;
    }[] = [];
    const today = todayInZone(props.timezone);

    for (let i = 0; i < 7; i++) {
      const d = new Date(sunday);
      d.setDate(d.getDate() + i);
      const iso = toLocalDateISO(d);
      days.push({
        date: iso,
        dayName: localeDate(dl(), d, { weekday: "short" }),
        dayNum: d.getDate(),
        isToday: iso === today,
        isPast: iso < today,
      });
    }
    return days;
  });

  const weekLabel = createMemo(() => {
    const days = weekDays();
    if (days.length === 0) return "";
    const first = new Date(days[0]!.date + "T12:00:00");
    const last = new Date(days[6]!.date + "T12:00:00");
    // Month and year in one call, so each language keeps its own order ("2026년 10월").
    const opts = { month: "long", year: "numeric" } as const;
    const fLabel = localeDate(dl(), first, opts);
    const lLabel = localeDate(dl(), last, opts);
    if (fLabel === lLabel) return fLabel;
    return `${fLabel} – ${lLabel}`;
  });

  // Group slots by display time — system picks first key
  const groupedSlots = createMemo((): GroupedSlot[] => {
    const raw = slots();
    if (raw.length === 0) return [];

    const map = new Map<string, TimeSlot[]>();
    for (const slot of raw) {
      const time = formatTimeInZone(slot.startTime, props.timezone, props.locale);
      const existing = map.get(time);
      if (existing) existing.push(slot);
      else map.set(time, [slot]);
    }

    return Array.from(map.entries())
      .map(([time, timeSlots]) => ({
        time,
        hour: getHourInZone(timeSlots[0]!.startTime, props.timezone),
        sortMinutes: getMinutesSinceMidnight(timeSlots[0]!.startTime, props.timezone),
        slots: timeSlots,
        key: timeSlots[0]!.key,
      }))
      .sort((a, b) => a.sortMinutes - b.sortMinutes);
  });

  const morningSlots = createMemo(() => groupedSlots().filter((s) => s.hour < 12));
  const afternoonSlots = createMemo(() => groupedSlots().filter((s) => s.hour >= 12));

  // Fetch all slots for the week when the viewed week changes
  let fetchVersion = 0;
  createEffect(
    on(
      () => [viewWeekStart(), reloadToken()] as const,
      ([weekStart]) => {
        const templateId = props.formData.appointmentTemplateId;
        const patientType = props.formData.patientType;
        if (!templateId || !patientType) return;

        const version = ++fetchVersion;
        const currentDate = selectedDate();
        const isInitial = initialLoading();
        const locationId = props.locationId;
        const providerId = props.formData.providerId;
        const holdId = props.slotHoldsEnabled ? props.formData.holdId : null;
        const onUpdate = props.onUpdate;
        setLoading(true);

        fetchWeekSlots(locationId, templateId, patientType, weekStart, providerId, holdId)
          .then((result) => {
            if (version !== fetchVersion) return;
            setWeekSlots((prev) => ({ ...prev, ...result.slotsByDate }));
            setNextAvailableDate(result.nextAvailableDate);

            if (isInitial) {
              const hasCurrentSlots = (result.slotsByDate[currentDate]?.length ?? 0) > 0;
              if (!hasCurrentSlots) {
                const firstAvailDay = Object.keys(result.slotsByDate)
                  .sort()
                  .find((d) => (result.slotsByDate[d]?.length ?? 0) > 0);

                const target = firstAvailDay ?? result.nextAvailableDate;
                if (target && target !== currentDate) {
                  setSelectedDate(target);
                  const newWeekStart = getSundayOf(target);
                  if (newWeekStart !== weekStart) {
                    setViewWeekStart(newWeekStart);
                    return;
                  }
                  onUpdate({
                    appointmentDate: target,
                    appointmentSlotKey: null,
                    appointmentSlotProviderName: null,
                  });
                }
              }
            }
            setInitialLoading(false);
          })
          .catch(() => {
            if (version !== fetchVersion) return;
            setInitialLoading(false);
          })
          .finally(() => {
            if (version !== fetchVersion) return;
            setLoading(false);
          });
      },
    ),
  );

  const handleDateChange = (date: string) => {
    if (date === selectedDate()) return;
    // The lost-race notice is about a time on the day being left behind.
    setHoldError(null);
    setSelectedDate(date);
    props.onUpdate({
      appointmentDate: date,
      appointmentSlotKey: null,
      appointmentSlotProviderName: null,
    });
    const newWeekStart = getSundayOf(date);
    if (newWeekStart !== viewWeekStart()) {
      setViewWeekStart(newWeekStart);
    }
  };

  // Picking a time IS committing here — there is no separate Next — so this is
  // where the slot is reserved. Advancing only on confirmation means a lost
  // race shows next to the grid, not at submit after everything is typed in.
  const handleSlotSelect = async (group: GroupedSlot) => {
    const templateId = props.formData.appointmentTemplateId;
    const patientType = props.formData.patientType;
    if (!templateId || !patientType || holdingSlot()) return;

    // With holds off there is nothing to reserve, so don't pay a round trip to
    // be told so — this is the behaviour the step had before holds existed.
    if (!props.slotHoldsEnabled) {
      props.onUpdate({
        appointmentSlotKey: group.key,
        appointmentSlotProviderName: group.slots[0]?.providerName ?? null,
      });
      props.onNext();
      return;
    }

    setHoldingSlot(group.key);
    setHoldError(null);
    try {
      const result = await placeHold(
        props.locationId,
        templateId,
        patientType,
        group.key,
        props.formData.holdId,
      );

      // Only the server refusing the slot stops the patient. If we simply
      // couldn't reserve it, carry on unreserved — submit arbitrates, as it
      // did before holds existed — rather than closing booking altogether.
      if (result.status === "taken") {
        setHoldError(t("slot_just_taken"));
        setReloadToken((n) => n + 1);
        return;
      }

      // Leave any previous token in place when we didn't get one: it is what
      // releases that slot on the next successful hold, and what stops the
      // patient colliding with their own claim if they pick it again.
      const changes: Partial<FormData> = {
        appointmentSlotKey: group.key,
        appointmentSlotProviderName: group.slots[0]?.providerName ?? null,
      };
      if (result.status === "held") changes.holdId = result.holdId;
      props.onUpdate(changes);
      props.onNext();
    } finally {
      setHoldingSlot(null);
    }
  };

  const shiftWeek = (direction: -1 | 1) => {
    const current = new Date(viewWeekStart() + "T12:00:00");
    current.setDate(current.getDate() + direction * 7);
    const newWeekStart = toLocalDateISO(current);
    if (newWeekStart < getSundayOf(todayInZone(props.timezone))) return;
    setViewWeekStart(newWeekStart);
  };

  const selectedDateInView = createMemo(() => {
    const date = selectedDate();
    return getSundayOf(date) === viewWeekStart() ? date : null;
  });

  // Swipe support
  let swipeRef: HTMLDivElement | undefined;
  useSwipe(
    () => swipeRef,
    (dir) => shiftWeek(dir === "left" ? 1 : -1),
  );

  // Slot list fade-in (only when slots weren't already visible)
  let slotsRef: HTMLDivElement | undefined;
  let hadSlotsBeforeLoad = false;
  createEffect(
    on(
      () => loading(),
      (isLoading, wasLoading) => {
        if (isLoading && !wasLoading) {
          hadSlotsBeforeLoad = groupedSlots().length > 0;
        }
        if (wasLoading && !isLoading && slotsRef && !hadSlotsBeforeLoad) {
          slotsRef.animate(
            [
              { opacity: 0, transform: "translateY(6px)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            { duration: 200, easing: "ease-out" },
          );
        }
      },
    ),
  );

  const findingTimesText = t("finding_times");

  return (
    <div class="flex flex-col gap-4" ref={swipeRef}>
      <h2 class="text-xl font-semibold text-typography-primary">{t("pick_a_time")}</h2>

      <Show
        when={!initialLoading()}
        fallback={
          <div class="flex items-center justify-center py-12">
            <div class="size-6 animate-spin rounded-full border-2 border-brand-subtle border-t-brand-primary" />
            <span class="ml-3 text-sm text-typography-secondary">{findingTimesText}</span>
          </div>
        }
      >
        {/* Week day strip */}
        <div class="flex flex-col gap-2">
          <div class="relative flex items-center justify-center">
            <span class="text-sm font-semibold text-typography-primary">{weekLabel()}</span>
            <div class="absolute right-0 flex gap-0.5">
              <button
                type="button"
                class="rounded-lg p-2 text-typography-secondary transition-colors hover:bg-brand-light hover:text-typography-primary disabled:opacity-30"
                onClick={() => shiftWeek(-1)}
                disabled={viewWeekStart() <= getSundayOf(todayInZone(props.timezone))}
                aria-label="Previous week"
              >
                <svg
                  class="size-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                class="rounded-lg p-2 text-typography-secondary transition-colors hover:bg-brand-light hover:text-typography-primary"
                onClick={() => shiftWeek(1)}
                aria-label="Next week"
              >
                <svg
                  class="size-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          <div class="grid grid-cols-7 gap-1">
            <For each={weekDays()}>
              {(day) => {
                const isSelected = () => day.date === selectedDateInView();
                const availRelevant = () => {
                  const avail = weekAvailability();
                  const dates = weekDays().map((d) => d.date);
                  return Object.keys(avail).some((k) => dates.includes(k));
                };
                const hasSlots = () => !availRelevant() || (weekAvailability()[day.date] ?? 0) > 0;
                const dimmed = () => !day.isPast && !hasSlots() && !loading();
                return (
                  <button
                    type="button"
                    disabled={day.isPast || dimmed()}
                    class={clsx(
                      "flex flex-col items-center gap-0.5 rounded-xl py-2.5 text-center transition-colors duration-150",
                      isSelected()
                        ? "bg-brand-primary text-white shadow-sm shadow-brand-primary/25"
                        : day.isPast || dimmed()
                          ? "text-typography-secondary/40"
                          : "text-typography-primary hover:bg-brand-light active:scale-[0.95]",
                      day.isToday && !isSelected() && "ring-1 ring-brand-primary/30",
                    )}
                    onClick={() => handleDateChange(day.date)}
                  >
                    <span
                      class={clsx(
                        "text-[10px] font-medium uppercase",
                        isSelected() ? "text-white/70" : "text-typography-secondary",
                      )}
                    >
                      {day.dayName}
                    </span>
                    <span class="text-sm font-semibold">{day.dayNum}</span>
                  </button>
                );
              }}
            </For>
          </div>

          {/* Selected date indicator */}
          <Show when={selectedDate()}>
            <p class="text-center text-sm text-typography-secondary">
              {formatDateShort(selectedDate(), props.locale)}
            </p>
          </Show>
        </div>

        {/* Time slots */}
        <div ref={slotsRef} class="flex flex-col gap-3">
          <Show when={holdError()}>
            <p class="rounded-xl bg-amber-50 px-4 py-3 text-center text-sm font-medium text-amber-800">
              {holdError()}
            </p>
          </Show>

          <Show
            when={!loading() || groupedSlots().length > 0}
            fallback={
              <div class="flex items-center justify-center py-8">
                <div class="size-6 animate-spin rounded-full border-2 border-brand-subtle border-t-brand-primary" />
                <span class="ml-3 text-sm text-typography-secondary">{t("finding_times")}</span>
              </div>
            }
          >
            <Show
              when={selectedDate()}
              fallback={
                <p class="py-6 text-center text-sm text-typography-secondary">{t("pick_a_time")}</p>
              }
            >
              <Show
                when={groupedSlots().length > 0}
                fallback={
                  <div class="rounded-xl bg-amber-50 px-4 py-6 text-center">
                    <p class="text-sm font-medium text-amber-800">{t("no_times_for_date")}</p>
                    <Show when={nextAvailableDate() && nextAvailableDate() !== selectedDate()}>
                      <button
                        type="button"
                        class="mt-2 text-sm font-semibold text-brand-primary hover:underline"
                        onClick={() => handleDateChange(nextAvailableDate()!)}
                      >
                        {t("jump_to_next")} · {formatDateShort(nextAvailableDate()!, props.locale)}
                      </button>
                    </Show>
                  </div>
                }
              >
                <Show when={morningSlots().length > 0}>
                  <div class="flex flex-col gap-2">
                    <span class="text-xs font-semibold tracking-wider text-typography-secondary uppercase">
                      {t("morning")}
                    </span>
                    <div class="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      <For each={morningSlots()}>
                        {(group) => (
                          <button
                            type="button"
                            class={clsx(
                              "rounded-xl border px-2 py-2.5 text-center text-sm font-medium transition-all duration-150 active:scale-[0.96]",
                              props.formData.appointmentSlotKey === group.key
                                ? "border-brand-primary bg-brand-primary text-white shadow-sm shadow-brand-primary/25"
                                : "border-brand-subtle text-typography-primary hover:border-brand-primary hover:bg-brand-light",
                            )}
                            disabled={holdingSlot() !== null}
                            onClick={() => void handleSlotSelect(group)}
                          >
                            {group.time}
                          </button>
                        )}
                      </For>
                    </div>
                  </div>
                </Show>

                <Show when={afternoonSlots().length > 0}>
                  <div class="flex flex-col gap-2">
                    <span class="text-xs font-semibold tracking-wider text-typography-secondary uppercase">
                      {t("afternoon")}
                    </span>
                    <div class="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      <For each={afternoonSlots()}>
                        {(group) => (
                          <button
                            type="button"
                            class={clsx(
                              "rounded-xl border px-2 py-2.5 text-center text-sm font-medium transition-all duration-150 active:scale-[0.96]",
                              props.formData.appointmentSlotKey === group.key
                                ? "border-brand-primary bg-brand-primary text-white shadow-sm shadow-brand-primary/25"
                                : "border-brand-subtle text-typography-primary hover:border-brand-primary hover:bg-brand-light",
                            )}
                            disabled={holdingSlot() !== null}
                            onClick={() => void handleSlotSelect(group)}
                          >
                            {group.time}
                          </button>
                        )}
                      </For>
                    </div>
                  </div>
                </Show>
              </Show>
            </Show>
          </Show>
        </div>
      </Show>

      <p class="text-sm text-typography-secondary">{t("cant_find_time")}</p>

      <div class="pt-2">
        <button
          type="button"
          class="rounded-full border border-brand-subtle px-4 py-2.5 text-sm font-medium text-typography-primary transition-colors hover:bg-brand-light active:scale-[0.98]"
          onClick={() => props.onBack()}
        >
          {t("back")}
        </button>
      </div>
    </div>
  );
}
